import type { FieldNode, GraphQLResolveInfo } from 'graphql';

import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { sanitizeUrl, type SafeUrl } from '@trello/safe-urls';
import { getCsrfRequestPayload } from '@trello/session-cookie/csrf';

import { safeFetch } from '../fetch';
import type {
  Automation,
  Automation_Type,
  AutomationLibrary,
  MemberAutomationsDataArgs,
  MutationDeleteAutomationArgs,
  MutationEnableAllLibraryAutomationsArgs,
  MutationImportAutomationLibraryArgs,
  MutationRenameAutomationLibraryArgs,
  MutationRunAutomationNowArgs,
  MutationShareAutomationLibraryArgs,
  MutationToggleAutomationArgs,
  MutationUnshareAutomationLibraryArgs,
  MutationUpdateAutomationArgs,
  QueryFetchLibraryAutomationsArgs,
} from '../generated';
import { isQueryInfo } from '../isQueryInfo';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type { QueryInfo, ResolverContext, TrelloRestResolver } from '../types';

export type ButlerResponseAutomation = {
  b: string[];
  cmd: string;
  id: string;
  lib?: string;
  t: number;
  type: string;
  uid: string;
};

export type ButlerResponseLibrary = {
  date: string;
  link_id: string;
  org_id: string;
  title: string;
  user_id: string;
  username: string;
};

const mapAutomationType = (type: string): Automation_Type => {
  switch (type) {
    case 'rule':
      return 'rule';
    case 'schedule':
      return 'schedule';
    case 'on-date':
      return 'onDate';
    default:
      throw new Error(`Unknown automation type: ${type}`);
  }
};

const safeFetchResult = async (
  context: ResolverContext,
  method: 'GET' | 'POST',
  safeApiUrl: SafeUrl,
  body?: Record<string, unknown>,
): Promise<Response> => {
  const result = await safeFetch(safeApiUrl, {
    method,
    credentials: 'include',
    headers: {
      ...(method === 'POST' && { 'Content-Type': 'application/json' }),
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    ...(body && {
      body: JSON.stringify({ ...body, ...getCsrfRequestPayload() }),
    }),
  });

  return result;
};

const handleNetworkError = async (
  result: Response,
  apiUrl: SafeUrl,
  context: ResolverContext,
) => {
  sendNetworkErrorEvent({
    url: apiUrl,
    response: await result.clone().text(),
    status: result.status,
    operationName: context.operationName,
  });

  throw await parseNetworkError(result);
};

const getRootNode = (info: GraphQLResolveInfo | QueryInfo): FieldNode => {
  return isQueryInfo(info) ? info.field : info.fieldNodes[0];
};

const transformAutomationsFromButler = (
  commands: ButlerResponseAutomation[],
) => {
  return commands.map((command) => {
    return {
      __typename: 'Automation' as const,
      automationNLString: command.cmd,
      enabledBoards: command.b,
      id: command.id,
      idMember: command.uid,
      lastModified: Number(command.t),
      libraryName: command.lib,
      type: mapAutomationType(command.type),
    };
  });
};

const transformLibrariesFromButler = (libraries: ButlerResponseLibrary[]) =>
  libraries.map((library) => ({
    __typename: 'AutomationLibrary' as const,
    containerId: library.org_id,
    memberId: library.user_id,
    shareId: library.link_id,
    name: library.title,
  }));

export const memberAutomationsResolver: TrelloRestResolver<
  MemberAutomationsDataArgs
> = async (_member, args, context, info) => {
  const rootNode = getRootNode(info);
  const { boardId } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-commands?b=${{
    value: boardId,
    type: 'boardId',
  }}`;

  const result = await safeFetchResult(context, 'GET', apiUrl, undefined);

  if (result.ok) {
    const { response, success } = await result.clone().json();

    if (success) {
      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];
      const libraries: AutomationLibrary[] = response?.libs
        ? transformLibrariesFromButler(response?.libs)
        : [];
      const model = {
        automations,
        libraries,
      };

      return prepareDataForApolloCache(model, rootNode, 'Member');
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const toggleAutomation: TrelloRestResolver<
  MutationToggleAutomationArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const { automationId, boardId, enable } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-commands/${{
    value: automationId,
    type: 'automationId',
  }}`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    op: 'enable',
    params: {
      board: { id: boardId },
      enabled: enable,
    },
  });

  if (result.ok) {
    const { response, success } = await result.clone().json();

    if (success) {
      const model: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];

      return prepareDataForApolloCache(model, rootNode);
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const runAutomationNow: TrelloRestResolver<
  MutationRunAutomationNowArgs
> = async (_object, args, context) => {
  const { automationId, boardId, timezone } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-run-command`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    cmd_id: automationId,
    board_data: {
      board_id: boardId,
      tz: timezone,
    },
  });

  if (result.ok) {
    const { success } = await result.clone().json();

    if (success) {
      return true;
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const deleteAutomation: TrelloRestResolver<
  MutationDeleteAutomationArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const { automationId, boardId } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-commands/${{
    value: automationId,
    type: 'automationId',
  }}`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    op: 'delete',
    params: {
      board: { id: boardId },
    },
  });

  if (result.ok) {
    const { success } = await result.clone().json();

    if (success) {
      return prepareDataForApolloCache({ id: automationId }, rootNode);
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const updateAutomation: TrelloRestResolver<
  MutationUpdateAutomationArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const {
    automationId,
    boardId,
    enabledBoards,
    automationNLString,
    libraryName,
    type,
  } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-commands/${{
    value: automationId,
    type: 'automationId',
  }}`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    op: 'update',
    params: {
      board: { id: boardId },
      command: {
        b: enabledBoards,
        cmd: automationNLString,
        lib: libraryName,
        type,
      },
    },
  });

  if (result.ok) {
    const { success, response } = await result.json();

    if (success) {
      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];

      return prepareDataForApolloCache(automations, rootNode);
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const enableAllLibraryAutomations: TrelloRestResolver<
  MutationEnableAllLibraryAutomationsArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const { boardId, boardName, boardUrl, workspaceId, enable, libraryName } =
    args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library-enable`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    board: {
      id: boardId,
      name: boardName,
      url: boardUrl,
    },
    enable,
    idOrganization: workspaceId,
    title: libraryName,
  });

  if (result.ok) {
    const { response, success } = await result.clone().json();

    if (success) {
      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];
      return prepareDataForApolloCache(automations, rootNode);
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const renameAutomationLibrary: TrelloRestResolver<
  MutationRenameAutomationLibraryArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const { boardId, library, newName } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library-rename`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    board: { id: boardId },
    new_title: newName,
    title: library?.name,
  });

  if (result.ok) {
    const { response, success } = await result.clone().json();

    if (success) {
      let newLibrary = null,
        oldLibrary = null;

      // Unshared libraries are not stored in cache
      if (library?.shareId) {
        newLibrary = {
          ...library,
          name: newName,
        };
        oldLibrary = {
          name: library?.name,
        };
      }

      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];
      return prepareDataForApolloCache(
        [
          {
            automations,
            library: newLibrary,
            oldLibrary,
          },
        ],
        rootNode,
      );
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const importAutomationLibrary: TrelloRestResolver<
  MutationImportAutomationLibraryArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const { boardId, workspaceId, shareId } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library-import/${{
    value: shareId,
    type: 'automationShareId',
  }}`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    board: { id: boardId },
    idOrganization: workspaceId,
  });

  if (result.ok) {
    const json = await result.clone().json();
    const { response, success } = json;
    if (success) {
      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];

      return prepareDataForApolloCache(automations, rootNode);
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const shareAutomationLibrary: TrelloRestResolver<
  MutationShareAutomationLibraryArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const { boardId, name } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library-share/`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    board: { id: boardId },
    title: name,
  });

  if (result.ok) {
    const json = await result.clone().json();
    const { response, success } = json;
    if (success && response?.lib) {
      const [library] = transformLibrariesFromButler([response.lib]);
      return prepareDataForApolloCache(library, rootNode);
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const unshareAutomationLibrary: TrelloRestResolver<
  MutationUnshareAutomationLibraryArgs
> = async (_object, args, context, info) => {
  const rootNode = getRootNode(info);
  const { boardId, shareId } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library-unshare/${{
    value: shareId,
    type: 'automationShareId',
  }}`;

  const result = await safeFetchResult(context, 'POST', apiUrl, {
    board: { id: boardId },
  });

  if (result.ok) {
    const json = await result.clone().json();
    const { response, success } = json;
    if (success && response?.lib) {
      const [library] = transformLibrariesFromButler([response.lib]);
      return prepareDataForApolloCache(library, rootNode);
    } else {
      await handleNetworkError(result, apiUrl, context);
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};

export const fetchLibraryAutomations: TrelloRestResolver<
  QueryFetchLibraryAutomationsArgs
> = async (_object, args, context, info) => {
  const { boardId, workspaceId, shareId } = args;

  const params = new URLSearchParams();
  params.set('b', boardId);
  params.set('idOrganization', workspaceId);
  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library/${{
    value: shareId,
    type: 'automationShareId',
  }}?${params}`;

  const result = await safeFetchResult(context, 'GET', apiUrl, undefined);

  if (result.ok) {
    const { response, success, error } = await result.clone().json();
    if (success) {
      const commands: ButlerResponseAutomation[] = response?.lib?.cmds || [];
      const automations: Automation[] =
        transformAutomationsFromButler(commands);

      return automations;
    } else {
      sendNetworkErrorEvent({
        url: apiUrl,
        response: error || 'Unknown error from Butler API',
        status: result.status,
        operationName: context.operationName,
      });
      throw new Error(error || 'Unknown error from Butler API');
    }
  } else {
    await handleNetworkError(result, apiUrl, context);
  }
};
